import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input, useAlert } from "@digital-services/ui";
import { isNotEmpty, isNotRegExMatch, maxLength, useInput } from "@ds-cps/ui";

import { useGetUniqueCheck } from "@organization/services/use-check-unique-organization-name";

const ORGANIZATION_NAME_CHARS_LIMIT = 256;

interface Props {
  label: string;
  value: string | undefined;
  placeholder: string;
  disabled?: boolean;
  organizationId?: string;
  onInputChange?: (newValue: string, isValid: boolean) => void;
  onBlur?: () => void;
}

export const OrganizationName: FunctionComponent<Props> = props => {
  const {
    label,
    value,
    placeholder,
    disabled,
    organizationId,
    onInputChange,
    onBlur
  } = props;

  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization"
  });

  const { notifyError } = useAlert();
  const { uniqueCheck } = useGetUniqueCheck();
  const [isUnique, setIsUnique] = useState<boolean>(true);

  const {
    value: organizationName,
    isValid,
    errorMessage,
    valueChangeHandler
  } = useInput(
    [
      maxLength(ORGANIZATION_NAME_CHARS_LIMIT, () =>
        tCommon("validation.string.max-length", {
          fieldName: label,
          maxLength: ORGANIZATION_NAME_CHARS_LIMIT
        })
      ),
      isNotEmpty(() =>
        tCommon("validation.string.not-empty", { fieldName: label })
      ),
      isNotRegExMatch(/[<>]/, () =>
        tCommon("validation.string.illegal-characters", {
          fieldName: label,
          characters: "<, >"
        })
      )
    ],
    value
  );

  let helperText: string | undefined;

  if (!isValid) {
    helperText = errorMessage;
  } else if (!isUnique) {
    helperText = t("unique.warning.message", {
      organization: organizationName
    });
  }

  return (
    <Input
      data-testid="organization-name"
      id="name"
      type="text"
      autoComplete="organization"
      label={label}
      placeholder={placeholder}
      error={!isValid}
      disabled={disabled}
      helperText={helperText}
      value={organizationName}
      onChange={event => {
        setIsUnique(true);
        const { value, isValid } = valueChangeHandler(event.target.value);
        if (onInputChange) onInputChange(value, isValid);
      }}
      onBlur={() => {
        if (organizationName) {
          const thenHandler = response => {
            if (!response || response instanceof Error) {
              notifyError({
                header: t("unique.error.message", { reason: response }),
                "data-testid": "unique-organization-check-alert"
              });
            } else if (response) {
              setIsUnique(response.status === 200);
            }
          };

          uniqueCheck(organizationName, organizationId)
            .then(thenHandler)
            .catch(() => {});
        }

        if (onBlur) onBlur();
      }}
    />
  );
};
